import React, { useState, useEffect, memo, useMemo } from "react";
import { Card, CardContent, CardMedia, Typography, Box, Modal, IconButton, Tooltip } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";

/** SALE.CHANNEL values that map to ECOM aging / ROS bucket (aligned with backend). */
const ECOM_METRICS_CHANNELS = new Set([
	"D2C",
	"D2C-INTERNATIONAL",
	"MP",
	"Marketplace",
	"Marketplace-INTERNATIONAL",
	"MP SOR",
	"MARKETPLACE OUTRIGHT",
	"LIQUIDATION",
	"CORPORATE",
]);

/** Maps API metrics channel to tooltip row key EBO | LFS | MBO | ECOM */
function metricsChannelToBucket(metricsChannel) {
	if (!metricsChannel) return null;
	if (metricsChannel === "EBO" || metricsChannel === "LFS" || metricsChannel === "MBO") return metricsChannel;
	if (ECOM_METRICS_CHANNELS.has(metricsChannel)) return "ECOM";
	return null;
}

/**
 * Scroll smoothly to the product card with the given PKEY and flash an
 * orange highlight ring for 1.8 s so the user can spot it instantly.
 */
function scrollToProductCard(pkey) {
	const id = `pict-card-${String(pkey).replace(/[^a-zA-Z0-9]/g, "-")}`;
	const el = document.getElementById(id);
	if (!el) return;
	el.scrollIntoView({ behavior: "smooth", block: "center" });
	el.style.transition = "outline 0.15s ease, box-shadow 0.15s ease";
	el.style.outline = "3px solid #e65100";
	el.style.borderRadius = "8px";
	el.style.boxShadow = "0 0 0 5px rgba(230,81,0,0.25)";
	setTimeout(() => {
		el.style.outline = "";
		el.style.boxShadow = "";
		el.style.borderRadius = "";
	}, 1800);
}

/**
 * Given a season like "AW-26" or "SS-26", return the ordered list of preceding
 * seasons to check as fallbacks.
 *   AW-26 → [SS-26, AW-25, SS-25, AW-24, SS-24, ...]
 *   SS-26 → [AW-25, SS-25, AW-24, SS-24, ...]
 */
function getSeasonFallbacks(season, max = 8) {
	const s = String(season || "").trim().toUpperCase();
	const m = s.match(/^(AW|SS)[- ]?(\d{2,4})$/);
	if (!m) return [];
	const type = m[1];
	const rawYear = m[2];
	const year = parseInt(rawYear.length === 4 ? rawYear.slice(-2) : rawYear, 10);
	const sep = s.includes("-") ? "-" : s.includes(" ") ? " " : "";
	const pad = (y) => String(y).padStart(rawYear.length, "0");
	const fallbacks = [];
	let t = type;
	let y = year;
	for (let i = 0; i < max; i++) {
		if (t === "AW") {
			t = "SS"; // SS of same year is immediately before AW of same year
		} else {
			t = "AW";
			y -= 1;
		}
		fallbacks.push(`${t}${sep}${pad(y)}`);
	}
	return fallbacks;
}

/** Short label for which sizes count as pivotal for this brand (matches backend rules). */
function pivotalSizesLabel(brandRaw) {
	const b = String(brandRaw || "")
		.toUpperCase()
		.trim();
	if (b === "RARE RABBIT" || b === "RAREISM") return "S, M, L & 30, 32, 34";
	if (b === "RARE ONES") return "9-10Y, 10-11Y, 11-12Y, 13-14Y";
	return "S, M, L";
}

function ProductCard({ product, metricsChannel = null, allProducts = [], onNavigateToProduct = null }) {
	const [open, setOpen] = useState(false);
	const [imageLoadError, setImageLoadError] = useState(false);
	const [recsOpen, setRecsOpen] = useState(false);

	const rawImageUrl = product.image_url ?? product.URL;
	const hasValidImage = (() => {
		if (rawImageUrl == null) return false;
		const s = String(rawImageUrl).trim();
		if (s === "" || s === "-" || s === "N/A") return false;
		const lower = s.toLowerCase();
		if (lower === "null" || lower === "undefined") return false;
		return true;
	})();

	/** Tall packshots (common for footwear) + cover/top crop hides the product; use contain + bottom bias when covering */
	const isDenimPants = (() => {
		const sub = String(product.sub_category || "").trim().toUpperCase();
		const cat = String(product.category || "").trim().toUpperCase();
		// category = IM.DEPARTMENT (e.g. "DENIM PANT"); sub_category = IM.SUB_CAT (e.g. "DENIM PANTS")
		return (
			sub === "DENIM PANTS" ||
			cat === "DENIM PANTS" ||
			sub.includes("DENIM PANT") ||
			cat.includes("DENIM PANT")
		);
	})();

	const formatStockCover = () => {
		const cover = product.stock_cover_period;
		if (cover == null || cover === 0 || Number.isNaN(Number(cover))) return "—";
		return `${Number(cover).toFixed(1)} days`;
	};

	const footwearLike = (() => {
		const d = String(product.division || "").toUpperCase();
		const cat = String(product.category || "").toUpperCase();
		const sub = String(product.sub_category || "").toUpperCase();
		return (
			d.includes("FOOT") ||
			cat.includes("SNEAKER") ||
			cat.includes("SHOE") ||
			cat.includes("FOOTWEAR") ||
			cat.includes("SANDAL") ||
			cat.includes("BOOT") ||
			sub.includes("SNEAKER") ||
			sub.includes("SHOE") ||
			sub.includes("FOOTWEAR")
		);
	})();

	/**
	 * Image stage height: mobile keeps clamp+vw; md+ uses aspect-ratio + caps so layout is stable for
	 * html2canvas PDF export (vw/clamp often mis-resolves in the cloned document).
	 */
	const imageStageSx = {
		position: "relative",
		width: "100%",
		...(footwearLike
			? {
					height: {
						xs: "clamp(200px, 58vw, 270px)",
						sm: "clamp(215px, 40vw, 300px)",
						md: "auto",
						lg: "auto",
						xl: "auto",
					},
					aspectRatio: { md: "5 / 6", lg: "5 / 6", xl: "5 / 6" },
					minHeight: { md: 200, lg: 200, xl: 200 },
					maxHeight: { md: 300, lg: 300, xl: 300 },
				}
			: {
					height: {
						xs: "clamp(160px, 50vw, 230px)",
						sm: "clamp(175px, 34vw, 260px)",
						md: "auto",
						lg: "auto",
						xl: "auto",
					},
					aspectRatio: { md: "4 / 5", lg: "4 / 5", xl: "4 / 5" },
					minHeight: { md: 170, lg: 170, xl: 170 },
					maxHeight: { md: 260, lg: 260, xl: 260 },
				}),
	};

	useEffect(() => {
		setImageLoadError(false);
	}, [rawImageUrl, product.PKEY]);

	const showImage = hasValidImage && !imageLoadError;

	const handleOpen = () => {
		if (showImage) setOpen(true);
	};
	const handleClose = () => setOpen(false);

	/** Fits card width (no horizontal scroll); small type + minmax(0,1fr) keeps columns aligned */
	const metricGridCellSx = {
		minWidth: 0,
		maxWidth: "100%",
		width: "100%",
		textAlign: "left",
		pr: { xs: 0.2, sm: 0.35 },
		boxSizing: "border-box",
	};
	const metricLabelTypoSx = {
		fontSize: { xs: "0.45rem", sm: "0.5rem", md: "0.52rem" },
		fontWeight: "bold",
		lineHeight: 1.2,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		maxWidth: "100%",
	};
	const metricValueTypoSx = {
		fontSize: { xs: "0.45rem", sm: "0.5rem", md: "0.52rem" },
		lineHeight: 1.2,
		maxWidth: "100%",
		minWidth: 0,
		overflowWrap: "anywhere",
		wordBreak: "break-word",
	};
	const metricGpValueTypoSx = {
		...metricValueTypoSx,
		fontSize: { xs: "0.4rem", sm: "0.46rem", md: "0.48rem" },
		fontVariantNumeric: "tabular-nums",
	};

	const formatGpValue = (value) => {
		if (value == null || value === "") return "0";
		const n = Number(String(value).replace(/,/g, ""));
		if (Number.isNaN(n)) return String(value);
		return n.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 0 });
	};

	/** Gross Profit per piece = GP Value ÷ Units Sold */
	const formatGpPerPiece = () => {
		const gpValue = parseFloat(String(product.lcogs?.gp_value || "0").replace(/,/g, "")) || 0;
		const unitsSold = parseFloat(product.troco_ls?.t_sale) || 0;
		if (!unitsSold) return "—";
		const val = Math.round(gpValue / unitsSold);
		return val.toLocaleString("en-IN");
	};

	/** MRP ÷ COGS (multiple); same inputs as MRP / COGS tiles */
	const formatMrpCogsMultiple = () => {
		const mrp = parseFloat(String(product.troco_ls?.mrp ?? "").replace(/,/g, "")) || 0;
		const cogs = parseFloat(String(product.COGS ?? "").replace(/,/g, "")) || 0;
		if (!cogs) return "—";
		return (mrp / cogs).toFixed(2);
	};

	// Format numbers with commas but no decimals
	const formatCurrency = (value) => {
		if (!value || value === "N/A") return "N/A";
		// Remove any existing commas and handle string "N/A"
		const cleanValue = value.toString().replace(/,/g, "");
		// Parse the number and round it
		const numValue = Math.round(parseFloat(cleanValue));
		// Check if it's a valid number
		if (isNaN(numValue)) return "N/A";
		// Format with commas but no decimals
		return numValue.toLocaleString("en-IN");
	};

	const formatNumber = (num) => {
		if (!num) return "0";
		return Math.round(Number(num)).toLocaleString("en-IN");
	};

	const agingBucket = metricsChannelToBucket(metricsChannel);

	// Prefer backend AVG_AGING (scoped to channel / store channel); else average non-zero channel aging
	const calculateAverageAging = () => {
		if (product.avg_aging != null && product.avg_aging !== "" && !Number.isNaN(Number(product.avg_aging))) {
			return Math.round(Number(product.avg_aging));
		}
		const agingValues = [product.ebo_aging || 0, product.lfs_aging || 0, product.mbo_aging || 0, product.ecom_aging || 0].filter(
			(value) => value > 0
		);
		if (agingValues.length === 0) return 0;
		return Math.round(agingValues.reduce((sum, value) => sum + value, 0) / agingValues.length);
	};

	// Calculate total WH SOH across all channels
	const calculateTotalWHSOH = () => {
		return product.total_wh_soh || 0;
	};

	// Calculate total EBO SOH across all channels
	const calculateTotalEBOSOH = () => {
		return product.total_ebo_soh || 0;
	};

	/** WH + EBO inventory for brand-specific pivotal sizes vs other sizes — from API */
	const calculatePivotalInv = () => Math.round(Number(product.inv_pivotal) || 0);
	const calculateNonPivotalInv = () => Math.round(Number(product.inv_non_pivotal) || 0);
	const pivotalWhSoh = () => Math.round(Number(product.pivotal_wh_soh) || 0);
	const pivotalEboSoh = () => Math.round(Number(product.pivotal_ebo_soh) || 0);
	const nonPivotalWhSoh = () => Math.round(Number(product.non_pivotal_wh_soh) || 0);
	const nonPivotalEboSoh = () => Math.round(Number(product.non_pivotal_ebo_soh) || 0);
	const pivotalAlphaWhSoh = () => Math.round(Number(product.pivotal_alpha_wh_soh) || 0);
	const pivotalAlphaEboSoh = () => Math.round(Number(product.pivotal_alpha_ebo_soh) || 0);
	const pivotalNumWhSoh = () => Math.round(Number(product.pivotal_num_wh_soh) || 0);
	const pivotalNumEboSoh = () => Math.round(Number(product.pivotal_num_ebo_soh) || 0);

	const brandUpper = String(product.brand || "").toUpperCase().trim();
	const isRRBrand = brandUpper === "RARE RABBIT" || brandUpper === "RAREISM";
	const hasNumPivotal = isRRBrand && (pivotalNumWhSoh() + pivotalNumEboSoh()) > 0;

	const sizeSohRows = Array.isArray(product.size_soh_breakdown) ? product.size_soh_breakdown : [];
	const sizeSohRowsSorted = [...sizeSohRows].sort((a, b) => {
		const pa = a.pivotal === true ? 0 : 1;
		const pb = b.pivotal === true ? 0 : 1;
		if (pa !== pb) return pa - pb;
		return String(a.size || "").localeCompare(String(b.size || ""));
	});

	// Determine if this product's pivotal sizes are broken (any pivotal size has 0 WH or 0 EBO)
	const pivotRows = sizeSohRows.filter((r) => r.pivotal || r.is_pivotal);
	const isWhBroken = pivotRows.length > 0 && pivotRows.some((r) => (r.wh_soh || 0) === 0);
	const isEboBroken = pivotRows.length > 0 && pivotRows.some((r) => (r.ebo_soh || 0) === 0);
	const isCurrentBroken = isWhBroken || isEboBroken;

	// Find similar products with matching attributes + non-broken pivotal, with season fallback
	const recommendations = useMemo(() => {
		if (!isCurrentBroken || !allProducts?.length) return { items: [], fromSeason: null };

		const norm = (v) => String(v || "").toUpperCase().trim();
		const myBrand   = norm(product.brand);
		const myCat     = norm(product.category);
		const mySubCat  = norm(product.sub_category);
		const myColor   = norm(product.color);
		const myPalette = norm(product.palette);

		const isNonBroken = (p) => {
			const altPivRows = Array.isArray(p.size_soh_breakdown)
				? p.size_soh_breakdown.filter((r) => r.pivotal || r.is_pivotal)
				: [];
			if (altPivRows.length === 0) {
				return (p.pivotal_wh_soh || 0) > 0 && (p.pivotal_ebo_soh || 0) > 0;
			}
			return altPivRows.every((r) => (r.wh_soh || 0) > 0 && (r.ebo_soh || 0) > 0);
		};

		const matchesForSeason = (targetSeason) =>
			allProducts
				.filter((p) => {
					if (p.PKEY === product.PKEY) return false;
					if (norm(p.brand)        !== myBrand)   return false;
					if (norm(p.category)     !== myCat)     return false;
					if (norm(p.sub_category) !== mySubCat)  return false;
					if (norm(p.color)        !== myColor)   return false;
					if (norm(p.palette)      !== myPalette) return false;
					if (norm(p.season)       !== norm(targetSeason)) return false;
					return isNonBroken(p);
				})
				.sort((a, b) =>
					((b.pivotal_wh_soh || 0) + (b.pivotal_ebo_soh || 0)) -
					((a.pivotal_wh_soh || 0) + (a.pivotal_ebo_soh || 0))
				)
				.slice(0, 4);

		// Try current season first, then each preceding season in order
		const seasonList = [product.season, ...getSeasonFallbacks(product.season)];
		for (const season of seasonList) {
			if (!season) continue;
			const found = matchesForSeason(season);
			if (found.length > 0) return { items: found, fromSeason: norm(season) };
		}

		return { items: [], fromSeason: null };
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCurrentBroken, allProducts, product.PKEY, product.brand, product.category,
		product.sub_category, product.color, product.palette, product.season]);

	// Calculate average DOH across all channels with non-zero values
	const calculateAverageDOH = () => {
		const dohValues = [product.ebo_doh || 0, product.lfs_doh || 0, product.mbo_doh || 0, product.ecom_doh || 0].filter(
			(value) => value > 0
		); // Only include non-zero values

		if (dohValues.length === 0) return 0;
		return Math.round((dohValues.reduce((sum, value) => sum + value, 0) / dohValues.length) * 10) / 10;
	};

	// Create tooltip content for DOH details with black and white theme
	const getDOHTooltipContent = () => {
		const channels = [
			{ name: "EBO", value: product.ebo_doh || 0 },
			{ name: "LFS", value: product.lfs_doh || 0 },
			{ name: "MBO", value: product.mbo_doh || 0 },
			{ name: "ECOM", value: product.ecom_doh || 0 },
		].filter((channel) => channel.value > 0); // Only show channels with non-zero DOH

		if (channels.length === 0) return "No DOH data available";

		return (
			<Box
				sx={{
					backgroundColor: "#ffffff",
					color: "#000000",
					p: 1.5,
					borderRadius: 1,
					minWidth: 200,
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					border: "1px solid #ffffff",
				}}>
				<Typography
					variant="body2"
					sx={{
						fontWeight: "bold",
						mb: 1.5,
						color: "#000000",
						fontSize: "0.8125rem",
						borderBottom: "1px solid #ffffff",
						pb: 0.5,
					}}>
					Channel-wise DOH:
				</Typography>
				{channels.map((channel, index) => (
					<Box
						key={index}
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							py: 0.3,
							borderBottom: index < channels.length - 1 ? "1px solid #cccccc" : "none",
						}}>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#666666",
								fontWeight: 500,
							}}>
							{channel.name}:
						</Typography>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#000000",
								fontWeight: "bold",
							}}>
							{channel.value} days
						</Typography>
					</Box>
				))}
			</Box>
		);
	};

	// Create tooltip content for T.SALE details with channel breakdown
	const getTSaleTooltipContent = () => {
		const channels = [
			{ name: "D2C", value: product.d2c_sold_qty || 0 },
			{ name: "Marketplace", value: product.marketplace_sold_qty || 0 },
			{ name: "LFS", value: product.lfs_sold_qty || 0 },
			{ name: "MBO", value: product.mbo_sold_qty || 0 },
			{ name: "EBO", value: product.ebo_sold_qty || 0 },
		].filter((channel) => channel.value > 0); // Only show channels with non-zero sales

		if (channels.length === 0) return "No sales data available";

		return (
			<Box
				sx={{
					backgroundColor: "#ffffff",
					color: "#000000",
					p: 1.5,
					borderRadius: 1,
					minWidth: 200,
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					border: "1px solid #ffffff",
				}}>
				<Typography
					variant="body2"
					sx={{
						fontWeight: "bold",
						mb: 1.5,
						color: "#000000",
						fontSize: "0.8125rem",
						borderBottom: "1px solid #ffffff",
						pb: 0.5,
					}}>
					Channel Sales Breakdown:
				</Typography>
				{channels.map((channel, index) => (
					<Box
						key={index}
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							py: 0.3,
							borderBottom: index < channels.length - 1 ? "1px solid #cccccc" : "none",
						}}>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#666666",
								fontWeight: 500,
							}}>
							{channel.name}:
						</Typography>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#000000",
								fontWeight: "bold",
							}}>
							{channel.value}
						</Typography>
					</Box>
				))}
			</Box>
		);
	};

	// Size rank for natural display order (numeric → youth → letter)
	// XXL aliases to same rank as 2XL so legacy payloads still sort correctly after label change to 2XL
	const LETTER_SIZE_RANK = { "2XS": 0, "XS": 1, "XXS": 2, "S": 3, "M": 4, "L": 5, "XL": 6, "XXL": 7, "2XL": 7, "3XL": 8, "4XL": 9, "5XL": 10 };
	const getSizeRank = (sz) => {
		const s = String(sz || "").toUpperCase().trim();
		const yM = s.match(/^(\d+)-\d+Y$/i);
		if (yM) return 200 + parseInt(yM[1]);
		if (/^\d+$/.test(s)) return 100 + parseInt(s);
		return s in LETTER_SIZE_RANK ? LETTER_SIZE_RANK[s] : 999;
	};

	// Create tooltip content for SOH details with black and white theme (matching Aging style)
	const getSOHTooltipContent = () => {
		const whSoh = calculateTotalWHSOH();
		const eboSoh = calculateTotalEBOSOH();
		const piv = calculatePivotalInv();
		const nonPiv = calculateNonPivotalInv();
		const npWh = nonPivotalWhSoh();
		const npEbo = nonPivotalEboSoh();

		if (whSoh === 0 && eboSoh === 0 && piv === 0 && nonPiv === 0) return "No SOH data available";

		// Sort sizes in natural display order
		const tableRows = [...sizeSohRows].sort((a, b) =>
			getSizeRank(a.size || a.size_label) - getSizeRank(b.size || b.size_label)
		);

		// Pivotal column range (contiguous block after sort)
		const firstPivIdx = tableRows.findIndex((r) => r.pivotal || r.is_pivotal);
		const lastPivIdx = tableRows.reduce((m, r, i) => (r.pivotal || r.is_pivotal ? i : m), -1);
		const pivTableRows = tableRows.filter((r) => r.pivotal || r.is_pivotal);
		const pivCount = firstPivIdx >= 0 ? lastPivIdx - firstPivIdx + 1 : 0;
		const prePivCount = firstPivIdx >= 0 ? firstPivIdx : 0;
		const postPivCount = firstPivIdx >= 0 ? tableRows.length - lastPivIdx - 1 : 0;

		// Broken = any pivotal size has 0 stock for that SOH source
		const eboBroken = pivTableRows.length > 0 && pivTableRows.some((r) => (r.ebo_soh || 0) === 0);
		const whBroken = pivTableRows.length > 0 && pivTableRows.some((r) => (r.wh_soh || 0) === 0);

		// Build legend labels from actual data (only show entries that exist)
		const alphaPivSizes = tableRows.filter((r) => r.is_alpha_pivotal).map((r) => r.size || r.size_label || "");
		const numPivSizes = tableRows.filter((r) => r.is_num_pivotal).map((r) => r.size || r.size_label || "");
		const alphaLegendLabel = alphaPivSizes.length > 0 ? alphaPivSizes.join("/") : "";
		const numLegendLabel = (() => {
			if (numPivSizes.length === 0) return "";
			if (numPivSizes.length === 1) return numPivSizes[0];
			const nums = numPivSizes.map((s) => parseFloat(s)).filter((n) => !isNaN(n));
			if (nums.length === numPivSizes.length) return `${Math.min(...nums)}–${Math.max(...nums)}`;
			return numPivSizes.join("/");
		})();

		const renderSOHTable = (label, field, broken) => {
			const brokenColor = broken ? "#c62828" : "#2e7d32";
			const pivBg = broken ? "#fff5f5" : "#f5fbf5";
			const pivBorder = broken ? "#ef9a9a" : "#a5d6a7";
			return (
				<Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
					{/* Title + badge */}
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.25 }}>
						<Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#222" }}>{label}</Typography>
						{pivCount > 0 && (
							<Typography sx={{
								fontSize: "0.56rem", px: 0.5, py: 0.1, borderRadius: 0.5,
								bgcolor: broken ? "#ffebee" : "#e8f5e9",
								color: brokenColor, fontWeight: 700, lineHeight: 1.5,
							}}>
								{broken ? "Broken" : "Non Broken"}
							</Typography>
						)}
					</Box>
					{/* Span header over pivotal columns */}
					{pivCount > 0 && (
						<Box sx={{ display: "flex", width: "100%", mb: "1px" }}>
							{prePivCount > 0 && <Box sx={{ flex: prePivCount }} />}
							<Box sx={{
								flex: pivCount, textAlign: "center",
								bgcolor: broken ? "#ffebee" : "#e8f5e9",
								borderRadius: "3px 3px 0 0", py: 0.15, mx: "1px",
							}}>
								<Typography sx={{ fontSize: "0.56rem", color: brokenColor, fontWeight: 700, lineHeight: 1 }}>
									{broken ? "Broken" : "Non Broken"}
								</Typography>
							</Box>
							{postPivCount > 0 && <Box sx={{ flex: postPivCount }} />}
						</Box>
					)}
					{/* Size header row */}
					<Box sx={{ display: "flex", width: "100%", borderBottom: `1px solid ${pivBorder}` }}>
						{tableRows.map((row, i) => {
							const isPiv = row.pivotal || row.is_pivotal;
							const isAlpha = row.is_alpha_pivotal;
							const isNum = row.is_num_pivotal;
							const leftEdge = isPiv && i === firstPivIdx;
							const rightEdge = isPiv && i === lastPivIdx;
							return (
								<Box key={i} sx={{
									flex: 1, textAlign: "center", py: 0.15,
									bgcolor: isPiv ? pivBg : "transparent",
									...(leftEdge && { borderLeft: `2px solid ${pivBorder}` }),
									...(rightEdge && { borderRight: `2px solid ${pivBorder}` }),
								}}>
									<Typography sx={{
										fontSize: "0.56rem",
										color: isAlpha ? "#1565c0" : isNum ? "#e65100" : isPiv ? "#2e7d32" : "#888",
										fontWeight: isPiv ? 700 : 400,
										lineHeight: 1.2,
									}}>
										{row.size || row.size_label || "?"}
									</Typography>
								</Box>
							);
						})}
					</Box>
					{/* Values row */}
					<Box sx={{ display: "flex", width: "100%" }}>
						{tableRows.map((row, i) => {
							const val = row[field] || 0;
							const isPiv = row.pivotal || row.is_pivotal;
							const leftEdge = isPiv && i === firstPivIdx;
							const rightEdge = isPiv && i === lastPivIdx;
							return (
								<Box key={i} sx={{
									flex: 1, textAlign: "center", py: 0.2,
									bgcolor: isPiv ? pivBg : "transparent",
									...(leftEdge && { borderLeft: `2px solid ${pivBorder}` }),
									...(rightEdge && { borderRight: `2px solid ${pivBorder}` }),
								}}>
									<Typography sx={{
										fontSize: "0.65rem",
										fontVariantNumeric: "tabular-nums",
										fontWeight: isPiv ? 700 : 400,
										color: (val === 0 && isPiv) ? "#c62828" : "#000",
									}}>
										{val}
									</Typography>
								</Box>
							);
						})}
					</Box>
				</Box>
			);
		};

		return (
			<Box
				sx={{
					backgroundColor: "#ffffff",
					color: "#000000",
					p: 1.5,
					borderRadius: 1,
					minWidth: 300,
					maxWidth: 520,
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					border: "1px solid #e0e0e0",
				}}>
				<Typography
					variant="body2"
					sx={{ fontWeight: "bold", mb: 0.75, color: "#000000", fontSize: "0.8125rem", borderBottom: "1px solid #cccccc", pb: 0.5 }}>
					Stock On Hand Details:
				</Typography>
				{/* Totals — same order as tables below: EBO (left) then WH (right) */}
				<Box sx={{ display: "flex", gap: 2, mb: 0.5 }}>
					<Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
						<Typography variant="body2" sx={{ fontSize: "0.7rem", color: "#666", fontWeight: 500 }}>EBO SOH:</Typography>
						<Typography variant="body2" sx={{ fontSize: "0.7rem", color: "#000", fontWeight: 700 }}>{formatNumber(eboSoh)}</Typography>
					</Box>
					<Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
						<Typography variant="body2" sx={{ fontSize: "0.7rem", color: "#666", fontWeight: 500 }}>WH SOH:</Typography>
						<Typography variant="body2" sx={{ fontSize: "0.7rem", color: "#000", fontWeight: 700 }}>{formatNumber(whSoh)}</Typography>
					</Box>
				</Box>

				{/* Size breakdown tables */}
				{tableRows.length > 0 && (
					<Box sx={{ mt: 0.5, pt: 0.5, borderTop: "1px solid #e0e0e0" }}>
						<Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
							{renderSOHTable("EBO SOH", "ebo_soh", eboBroken)}
							<Box sx={{ width: "1px", bgcolor: "#d0d0d0", alignSelf: "stretch" }} />
							{renderSOHTable("WH SOH", "wh_soh", whBroken)}
						</Box>
					{/* Legend — only show entries that actually appear in the data */}
					{(alphaPivSizes.length > 0 || numPivSizes.length > 0) && (
						<Box sx={{ display: "flex", gap: 1.5, mt: 0.5, flexWrap: "wrap" }}>
							{alphaPivSizes.length > 0 && (
								<Typography sx={{ fontSize: "0.56rem", color: "#1565c0" }}>
									■ Alpha pivotal ({alphaLegendLabel})
								</Typography>
							)}
							{numPivSizes.length > 0 && (
								<Typography sx={{ fontSize: "0.56rem", color: "#e65100" }}>
									■ Numeric pivotal ({numLegendLabel})
								</Typography>
							)}
						</Box>
					)}
					</Box>
				)}

				{/* Non-pivotal summary — EBO then WH to match table columns */}
				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.75, pt: 0.5, borderTop: "1px solid #e0e0e0" }}>
					<Typography sx={{ fontSize: "0.65rem", color: "#888" }}>Non-pivotal EBO · WH</Typography>
					<Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#000" }}>
						{formatNumber(npEbo)} · {formatNumber(npWh)}
					</Typography>
				</Box>
			</Box>
		);
	};

	// Create tooltip content for channel-level aging details with black and white theme
	const getAgingTooltipContent = () => {
		const rows = [
			{ name: "EBO", value: product.ebo_aging || 0 },
			{ name: "LFS", value: product.lfs_aging || 0 },
			{ name: "MBO", value: product.mbo_aging || 0 },
			{ name: "ECOM", value: product.ecom_aging || 0 },
		];
		const channels = rows
			.filter((ch) => ch.value > 0)
			.filter((ch) => !agingBucket || ch.name === agingBucket);

		if (channels.length === 0) return "No aging data available";

		return (
			<Box
				sx={{
					backgroundColor: "#ffffff",
					color: "#000000",
					p: 1.5,
					borderRadius: 1,
					minWidth: 200,
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					border: "1px solid #ffffff",
				}}>
				<Typography
					variant="body2"
					sx={{
						fontWeight: "bold",
						mb: 1.5,
						color: "#000000",
						fontSize: "0.8125rem",
						borderBottom: "1px solid #ffffff",
						pb: 0.5,
					}}>
					Channel-wise Aging:
				</Typography>
				{channels.map((channel, index) => (
					<Box
						key={index}
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							py: 0.3,
							borderBottom: index < channels.length - 1 ? "1px solid #cccccc" : "none",
						}}>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#666666",
								fontWeight: 500,
							}}>
							{channel.name}:
						</Typography>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.75rem",
								color: "#000000",
								fontWeight: "bold",
							}}>
							{channel.value} days
						</Typography>
					</Box>
				))}
			</Box>
		);
	};

	// Create tooltip content for ROS details with channel-level breakdown
	const getROSTooltipContent = () => {
		// Calculate ROS for each channel based on aging and sales data
		const rows = [
			{
				name: "EBO",
				aging: product.ebo_aging || 0,
				sales: product.ebo_sold_qty || 0,
				ros: product.ebo_aging > 0 ? Math.round((product.ebo_sold_qty || 0) / product.ebo_aging) : 0,
			},
			{
				name: "LFS",
				aging: product.lfs_aging || 0,
				sales: product.lfs_sold_qty || 0,
				ros: product.lfs_aging > 0 ? Math.round((product.lfs_sold_qty || 0) / product.lfs_aging) : 0,
			},
			{
				name: "MBO",
				aging: product.mbo_aging || 0,
				sales: product.mbo_sold_qty || 0,
				ros: product.mbo_aging > 0 ? Math.round((product.mbo_sold_qty || 0) / product.mbo_aging) : 0,
			},
			{
				name: "ECOM",
				aging: product.ecom_aging || 0,
				sales: product.ecom_sold_qty || 0,
				ros: product.ecom_aging > 0 ? Math.round((product.ecom_sold_qty || 0) / product.ecom_aging) : 0,
			},
		];
		const channels = rows
			.filter((channel) => channel.aging > 0)
			.filter((channel) => !agingBucket || channel.name === agingBucket);

		if (channels.length === 0) return "No ROS data available";

		return (
			<div
				style={{
					backgroundColor: "#ffffff",
					color: "#000000",
					padding: "12px",
					borderRadius: "4px",
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					border: "1px solid #ffffff",
					minWidth: "250px",
					fontFamily: "Inter, sans-serif",
				}}>
				<div
					style={{
						fontWeight: "bold",
						marginBottom: "12px",
						color: "#000000",
						fontSize: "11px",
						borderBottom: "1px solid #e0e0e0",
						paddingBottom: "4px",
					}}>
					Channel-wise ROS (Rate of Sale):
				</div>
				{channels.map((channel, index) => (
					<div
						key={index}
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							padding: "4px 0",
							borderBottom: index < channels.length - 1 ? "1px solid #e0e0e0" : "none",
						}}>
						<span
							style={{
								fontSize: "11px",
								color: "#666666",
								fontWeight: 500,
							}}>
							{channel.name}:
						</span>
						<span
							style={{
								fontSize: "11px",
								color: "#000000",
								fontWeight: "bold",
							}}>
							{channel.ros} ({channel.sales} units / {channel.aging} days)
						</span>
					</div>
				))}
			</div>
		);
	};

	return (
		<>
			<Card
				sx={{
					height: "100%",
					display: "flex",
					flexDirection: "column",
					borderRadius: 0,
					boxShadow: "none",
					border: "none",
					"& .MuiCardMedia-root": {
						borderRadius: 0,
					},
				}}>
				<Box
					data-pdf-image-stage=""
					data-pdf-field="image"
					sx={{
						...imageStageSx,
						flexShrink: 0,
						...(showImage
							? {
									overflow: "hidden",
								}
							: {}),
						...(showImage && footwearLike
							? {
									backgroundColor: "#ebebeb",
								}
							: {}),
						...(showImage
							? {
									"&:hover .zoom-icon": {
										opacity: 1,
									},
								}
							: {}),
					}}
				>
			{/* Broken pivotal badge — top-left overlay on the image */}
			{isCurrentBroken && (
				<Box sx={{
					position: "absolute", top: 4, left: 4, zIndex: 2,
					display: "flex", flexDirection: "column", gap: "2px",
					pointerEvents: "none",
				}}>
					{/* WH broken chip */}
					{isWhBroken && (
						<Box sx={{
							bgcolor: "rgba(21,101,192,0.88)", color: "#fff",
							fontSize: "0.48rem", fontWeight: 700, px: 0.6, py: 0.2,
							borderRadius: 0.5, lineHeight: 1.4, letterSpacing: "0.02em",
						}}>
							⚠ WH BROKEN
						</Box>
					)}
					{/* EBO broken chip */}
					{isEboBroken && (
						<Box sx={{
							bgcolor: "rgba(198,40,40,0.88)", color: "#fff",
							fontSize: "0.48rem", fontWeight: 700, px: 0.6, py: 0.2,
							borderRadius: 0.5, lineHeight: 1.4, letterSpacing: "0.02em",
						}}>
							⚠ EBO BROKEN
						</Box>
					)}
				</Box>
			)}
				{showImage ? (
					<>
						<CardMedia
							component="img"
							image={rawImageUrl}
							alt={product.PKEY}
							onClick={handleOpen}
							onError={() => setImageLoadError(true)}
								sx={{
									display: "block",
									width: "100%",
									height: "100%",
									maxWidth: "100%",
									maxHeight: "100%",
									// Footwear: tall canvas + white top — cover + bottom anchor crops empty space, fills tile (no tiny shoe + overlap)
									...(footwearLike
										? {
												objectFit: "cover",
												objectPosition: "center bottom",
											}
										: {
												objectFit: "cover",
												objectPosition: "center center",
											}),
									borderRadius: 0,
									cursor: "pointer",
								}}
							/>
							<IconButton
								className="zoom-icon"
								onClick={handleOpen}
								sx={{
									position: "absolute",
									left: "50%",
									...(footwearLike
										? {
												bottom: 6,
												top: "auto",
												transform: "translateX(-50%)",
											}
										: {
												top: "50%",
												transform: "translate(-50%, -50%)",
											}),
									backgroundColor: "rgba(0, 0, 0, 0.5)",
									color: "white",
									opacity: 0,
									transition: "opacity 0.3s ease",
									"&:hover": {
										backgroundColor: "rgba(0, 0, 0, 0.7)",
									},
								}}>
								<ZoomInIcon />
							</IconButton>
						</>
					) : (
						<Box
							sx={{
								height: "100%",
								width: "100%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								backgroundColor: "#f5f5f5",
								borderBottom: "1px solid #e0e0e0",
								color: "#757575",
								px: 2,
							}}>
							<Typography variant="body2" sx={{ fontWeight: 500, textAlign: "center", fontSize: "0.8125rem" }}>
								{hasValidImage && imageLoadError ? "Image could not be loaded" : "No Image Found"}
							</Typography>
						</Box>
					)}
				</Box>
				<CardContent
					sx={{
						p: 1,
						"&:last-child": { pb: 1 },
						borderRadius: 0,
					}}>
					<Box data-pdf-field="option">
						<Typography
							variant="subtitle2"
							gutterBottom
							sx={{ fontWeight: "bold", mb: 0.5, textAlign: "center", fontSize: "0.75rem" }}>
							OPTION: {product.PKEY}
							{isDenimPants && (
								<Box component="span" data-pdf-field="fit_type" sx={{ fontWeight: "bold" }}>
									{" "}
									· FIT: {product.fitType || "N/A"}
								</Box>
							)}
						</Typography>
					</Box>

					<Box
						data-pdf-metrics-grid=""
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
							rowGap: { xs: 0.35, sm: 0.5 },
							columnGap: { xs: 0.25, sm: 0.45 },
							maxWidth: "100%",
							alignItems: "start",
							justifyItems: "stretch",
						}}>
						{/* Row 1 */}
						<Box data-pdf-field="season" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								Season:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{product.season || "N/A"}
							</Typography>
						</Box>
						<Box data-pdf-field="category" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								Category:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{product.category || "N/A"}
							</Typography>
						</Box>
						<Box data-pdf-field="mrp" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								MRP:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{formatCurrency(product.troco_ls?.mrp)}
							</Typography>
						</Box>
						<Box data-pdf-field="cogs" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								COGS:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{formatCurrency(product.COGS)}
							</Typography>
						</Box>
						<Box data-pdf-field="tinw" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								T.INW:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{formatNumber(product.troco_ls?.t_inw)}
							</Typography>
						</Box>
						<Box data-pdf-field="multi_mrp_cogs" sx={metricGridCellSx} title="MRP ÷ COGS">
							<Typography variant="body2" sx={metricLabelTypoSx}>
								Multi:
							</Typography>
							<Typography
								variant="body2"
								sx={{ ...metricValueTypoSx, fontVariantNumeric: "tabular-nums" }}>
								{formatMrpCogsMultiple()}
							</Typography>
						</Box>

						{/* Row 2 */}
						<Box data-pdf-field="sold_in_period" sx={metricGridCellSx}>
							<Tooltip
								title={getTSaleTooltipContent()}
								arrow
								placement="top"
								slotProps={{
									tooltip: {
										sx: {
											backgroundColor: "transparent",
											border: "none",
											boxShadow: "none",
											"& .MuiTooltip-arrow": {
												color: "#ffffff",
											},
										},
									},
								}}>
								<Box sx={{ cursor: "help", minWidth: 0, maxWidth: "100%" }}>
									<Typography variant="body2" sx={metricLabelTypoSx}>
										Sold In Period:
									</Typography>
									<Typography variant="body2" sx={metricValueTypoSx}>
										{formatNumber(product.troco_ls?.t_sale)}
									</Typography>
								</Box>
							</Tooltip>
						</Box>
						<Box data-pdf-field="total_sold" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								Total Sold:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{formatNumber(product.troco_ls?.t_sale_total)}
							</Typography>
						</Box>
						<Box data-pdf-field="balance" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								Balance:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{formatNumber(product.troco_ls?.balance)}
							</Typography>
						</Box>
						<Box data-pdf-field="st" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								ST:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{Number(product.troco_ls?.st ?? 0).toFixed(2)}%
							</Typography>
						</Box>
						<Box data-pdf-field="gp_pct" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								GP%:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{product.lcogs?.gp_percentage || "0.00%"}
							</Typography>
						</Box>
						<Box data-pdf-field="online" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								Online:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{product.lcogs?.oln_cont || "0%"} ({product.online_sold_qty || 0})
							</Typography>
						</Box>
						<Box data-pdf-field="offline" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								Offline:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{product.lcogs?.offln_cont || "0%"} ({product.offline_sold_qty || 0})
							</Typography>
						</Box>
					<Box data-pdf-field="gp_value" sx={metricGridCellSx}>
						<Typography variant="body2" sx={metricLabelTypoSx}>
							GP Value:
						</Typography>
						<Typography variant="body2" sx={metricGpValueTypoSx}>
							{formatGpValue(product.lcogs?.gp_value)}
						</Typography>
					</Box>
					<Box data-pdf-field="gp_per_piece" sx={metricGridCellSx}>
						<Typography variant="body2" sx={metricLabelTypoSx}>
							GP/Pc:
						</Typography>
						<Typography variant="body2" sx={metricGpValueTypoSx}>
							{formatGpPerPiece()}
						</Typography>
					</Box>

					{/* Row 3 */}
						<Box data-pdf-field="soh" sx={metricGridCellSx}>
							<Tooltip
								title={getSOHTooltipContent()}
								arrow
								placement="top"
								slotProps={{
									tooltip: {
										sx: {
											backgroundColor: "transparent",
											border: "none",
											boxShadow: "none",
											"& .MuiTooltip-arrow": {
												color: "#ffffff",
											},
										},
									},
								}}>
								<Box sx={{ cursor: "help", minWidth: 0, maxWidth: "100%" }}>
									<Typography variant="body2" sx={metricLabelTypoSx}>
										SOH:
									</Typography>
									<Typography variant="body2" sx={metricValueTypoSx}>
										{formatNumber(calculateTotalWHSOH() + calculateTotalEBOSOH())}
									</Typography>
								</Box>
							</Tooltip>
						</Box>
						<Box data-pdf-field="avg_aging" sx={metricGridCellSx}>
							<Tooltip
								title={getAgingTooltipContent()}
								arrow
								placement="top"
								slotProps={{
									tooltip: {
										sx: {
											backgroundColor: "transparent",
											border: "none",
											boxShadow: "none",
											"& .MuiTooltip-arrow": {
												color: "#ffffff",
											},
										},
									},
								}}>
								<Box sx={{ cursor: "help", minWidth: 0, maxWidth: "100%" }}>
									<Typography variant="body2" sx={metricLabelTypoSx}>
										Avg Aging:
									</Typography>
									<Typography variant="body2" sx={metricValueTypoSx}>
										{calculateAverageAging()} days
									</Typography>
								</Box>
							</Tooltip>
						</Box>
						<Box data-pdf-field="avg_doh" sx={metricGridCellSx}>
							<Tooltip
								title={getDOHTooltipContent()}
								arrow
								placement="top"
								slotProps={{
									tooltip: {
										sx: {
											backgroundColor: "transparent",
											border: "none",
											boxShadow: "none",
											"& .MuiTooltip-arrow": {
												color: "#ffffff",
											},
										},
									},
								}}>
								<Box sx={{ cursor: "help", minWidth: 0, maxWidth: "100%" }}>
									<Typography variant="body2" sx={metricLabelTypoSx}>
										Avg DOH:
									</Typography>
									<Typography variant="body2" sx={metricValueTypoSx}>
										{calculateAverageDOH()} days
									</Typography>
								</Box>
							</Tooltip>
						</Box>
						<Box data-pdf-field="period_ros" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx} title="Sold in period ÷ days in range">
								Period ROS:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{product.period_ros != null && product.period_ros > 0
									? Number(product.period_ros).toFixed(2)
									: "—"}
							</Typography>
						</Box>
						<Box data-pdf-field="stock_cover_period" sx={metricGridCellSx}>
							<Typography variant="body2" sx={metricLabelTypoSx} title="SOH ÷ period ROS">
								Stock Cover:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{formatStockCover()}
							</Typography>
						</Box>
						<Box data-pdf-field="ros" sx={metricGridCellSx}>
							<Tooltip
								title={getROSTooltipContent()}
								arrow
								placement="top"
								slotProps={{
									tooltip: {
										sx: {
											backgroundColor: "transparent",
											border: "none",
											boxShadow: "none",
											"& .MuiTooltip-arrow": {
												color: "#ffffff",
											},
										},
									},
								}}>
								<Box sx={{ cursor: "help", minWidth: 0, maxWidth: "100%" }}>
									<Typography variant="body2" sx={metricLabelTypoSx}>
										ROS:
									</Typography>
									<Typography variant="body2" sx={metricValueTypoSx}>
										{Math.round(product.rate_of_sale || 0)}
									</Typography>
								</Box>
							</Tooltip>
						</Box>

					{/* Row 4 — full width so BLEND lines up under the grid */}
						<Box data-pdf-field="blend" sx={{ ...metricGridCellSx, gridColumn: "1 / -1" }}>
							<Typography variant="body2" sx={metricLabelTypoSx}>
								BLEND:
							</Typography>
							<Typography variant="body2" sx={metricValueTypoSx}>
								{product.blend || "N/A"}
							</Typography>
						</Box>
				</Box>

				{/* Recommendations — shown only when pivotal sizes are broken */}
				{isCurrentBroken && recommendations.items.length > 0 && (
					<Box sx={{ mt: 0.75, pt: 0.5, borderTop: "2px solid #ffcc80" }}>
						<Box
							onClick={() => setRecsOpen((prev) => !prev)}
							sx={{
								display: "flex", alignItems: "center", justifyContent: "space-between",
								cursor: "pointer", mb: recsOpen ? 0.5 : 0,
							}}>
							<Box>
								<Typography sx={{ fontSize: "0.58rem", color: "#e65100", fontWeight: 700, lineHeight: 1.3 }}>
									Similar Options — Full Pivotal Stock ({recommendations.items.length})
								</Typography>
								{recommendations.fromSeason && recommendations.fromSeason !== String(product.season || "").toUpperCase().trim() && (
									<Typography sx={{ fontSize: "0.5rem", color: "#888", lineHeight: 1.2 }}>
										from season: {recommendations.fromSeason}
									</Typography>
								)}
							</Box>
							<Typography sx={{ fontSize: "0.58rem", color: "#e65100", ml: 0.5, flexShrink: 0 }}>
								{recsOpen ? "▲" : "▼"}
							</Typography>
						</Box>
						{recsOpen && recommendations.items.map((rec, idx) => {
							const recImg = rec.image_url ?? rec.URL;
							const hasRecImg = recImg && !["", "-", "N/A", "null", "undefined"].includes(String(recImg).trim().toLowerCase());
							const recPivTotal = (rec.pivotal_wh_soh || 0) + (rec.pivotal_ebo_soh || 0);
							return (
								<Box
									key={rec.PKEY}
									onClick={() => onNavigateToProduct ? onNavigateToProduct(rec.PKEY) : scrollToProductCard(rec.PKEY)}
									sx={{
										display: "flex", alignItems: "center", gap: 0.75,
										py: 0.4, px: 0.25,
										borderBottom: idx < recommendations.items.length - 1 ? "1px solid #f5f5f5" : "none",
										bgcolor: idx % 2 === 0 ? "#fffde7" : "#fff8e1",
										borderRadius: 0.5,
										cursor: "pointer",
										transition: "background-color 0.15s",
										"&:hover": { bgcolor: "#ffe082" },
									}}
									title="Click to jump to this option"
								>
									{/* Thumbnail */}
									<Box sx={{
										width: 36, height: 36, flexShrink: 0,
										border: "1px solid #e0e0e0", borderRadius: 0.5,
										overflow: "hidden", bgcolor: "#fafafa",
									}}>
										{hasRecImg ? (
											<img src={recImg} alt={rec.PKEY} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
										) : (
											<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
												<Typography sx={{ fontSize: "0.38rem", color: "#bbb" }}>N/A</Typography>
											</Box>
										)}
									</Box>
									{/* Style / color / season */}
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography sx={{ fontSize: "0.55rem", fontWeight: 700, color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
											{rec.style || rec.PKEY}
										</Typography>
										<Typography sx={{ fontSize: "0.5rem", color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
											{[rec.color, rec.palette].filter(Boolean).join(" · ")}
										</Typography>
										{rec.season && String(rec.season).toUpperCase() !== recommendations.fromSeason && (
											<Typography sx={{ fontSize: "0.46rem", color: "#aaa" }}>{rec.season}</Typography>
										)}
									</Box>
									{/* Pivotal SOH + jump arrow */}
									<Box sx={{ flexShrink: 0, textAlign: "right" }}>
										<Typography sx={{ fontSize: "0.56rem", color: "#2e7d32", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
											{recPivTotal}
										</Typography>
										<Typography sx={{ fontSize: "0.44rem", color: "#888" }}>
											{rec.pivotal_wh_soh || 0}W · {rec.pivotal_ebo_soh || 0}E
										</Typography>
									</Box>
									<Typography sx={{ fontSize: "0.6rem", color: "#bbb", flexShrink: 0, ml: 0.25 }}>↗</Typography>
								</Box>
							);
						})}
					</Box>
				)}
			</CardContent>
		</Card>

		{/* Zoom Modal */}
			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="image-modal"
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					"& .MuiBackdrop-root": {
						backgroundColor: "rgba(0, 0, 0, 0.9)",
					},
				}}>
				<Box
					sx={{
						maxWidth: "90vw",
						maxHeight: "90vh",
						outline: "none",
						bgcolor: "background.paper",
						p: 1,
					}}
					onClick={handleClose}>
					{showImage ? (
						<img
							src={rawImageUrl}
							alt={product.PKEY}
							style={{
								width: "100%",
								height: "100%",
								maxWidth: "100%",
								maxHeight: "90vh",
								objectFit: "contain",
								objectPosition: footwearLike ? "center bottom" : "center center",
							}}
						/>
					) : (
						<Typography sx={{ p: 4, color: "text.secondary" }}>No Image Found</Typography>
					)}
				</Box>
			</Modal>
		</>
	);
}

export default memo(ProductCard);
